import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { TabType } from '../types';
import { useTranslation } from 'react-i18next';

interface HeaderProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  systemConfig: {
    targetWeight: number;
  };
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (open: boolean) => void;
}

const Header: React.FC<HeaderProps> = ({
  activeTab,
  onTabChange,
  systemConfig,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
}) => {
  const { theme, toggleTheme, colors } = useTheme();
  const { t, i18n } = useTranslation();

  const tabs = [
    { id: 'system-config', label: t('navigation.config') },
    { id: 'motor-database', label: t('navigation.motor') },
    { id: 'battery-database', label: t('navigation.battery') },
    { id: 'propeller-database', label: t('navigation.propeller') },
    { id: 'analysis-results', label: t('navigation.analysis') },
    { id: 'optimization', label: t('navigation.optimization') },
    { id: 'data-management', label: t('navigation.data') },
  ] as const;

  const toggleLanguage = () => {
    const currentLang = i18n.language?.startsWith('zh') ? 'zh' : 'en';
    const newLang = currentLang === 'en' ? 'zh' : 'en';
    i18n.changeLanguage(newLang);
  };

  return (
    <header className="relative">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <h1 
              className="text-lg font-bold"
              style={{ 
                color: colors.text,
                fontFamily: '"Forma DJR Display", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                fontWeight: 600,
                letterSpacing: '0.08em',
                fontSize: '18px'
              }}
            >
              P.N. 0518
            </h1>
          </div>

          {/* 中央导航 - 自定义极简导航 */}
          <div className="hidden md:flex items-center justify-center flex-1">
            <nav className="flex items-center space-x-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => onTabChange(tab.id)}
                  className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                    activeTab === tab.id 
                      ? 'transform scale-95' 
                      : 'hover:scale-105 hover:bg-opacity-60'
                  }`}
                  style={{
                    backgroundColor: activeTab === tab.id ? colors.accent : 'transparent',
                    color: activeTab === tab.id ? 'white' : colors.textSecondary,
                    boxShadow: activeTab === tab.id ? '0 4px 12px rgba(0,0,0,0.15)' : 'none'
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* 右侧控制区域 */}
          <div className="flex items-center space-x-3">
            {/* 系统信息 - 桌面端显示 */}
            <div className="hidden lg:flex items-center space-x-2">
              <span className="text-xs" style={{ color: colors.textMuted }}>
                {t('systemConfig.targetWeight')}:
              </span>
              <span className="text-xs font-medium" style={{ color: colors.text }}>
                {systemConfig.targetWeight}kg
              </span>
            </div>

            {/* 语言切换按钮 */}
            <button
              onClick={toggleLanguage}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
              style={{ 
                backgroundColor: 'transparent',
                color: colors.textMuted,
              }}
              title={t('tooltips.language')}
            >
              <span className="text-sm font-medium">
                {i18n.language?.startsWith('zh') ? '中' : 'EN'}
              </span>
            </button>

            {/* 主题切换按钮 */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
              style={{ 
                backgroundColor: 'transparent',
                color: colors.textMuted,
              }}
              title={t('tooltips.darkMode')}
            >
              {theme === 'light' ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              )}
            </button>

            {/* 移动端菜单按钮 */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg transition-all duration-200"
              style={{ 
                backgroundColor: 'transparent',
                color: colors.textMuted,
              }}
              aria-label="菜单"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* 移动端导航菜单 */}
      {isMobileMenuOpen && (
        <div 
          className="md:hidden absolute top-full left-0 right-0 z-50 backdrop-blur-sm"
          style={{ 
            backgroundColor: `${colors.surface}f0`,
          }}
        >
          <div className="max-w-7xl mx-auto px-4 py-4">
            <nav className="grid grid-cols-2 gap-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    onTabChange(tab.id);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`p-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                    activeTab === tab.id ? 'scale-95' : 'hover:scale-105'
                  }`}
                  style={{
                    backgroundColor: activeTab === tab.id ? colors.accent : colors.surface,
                    color: activeTab === tab.id ? 'white' : colors.text,
                    boxShadow: activeTab === tab.id ? '0 4px 12px rgba(0,0,0,0.15)' : 'none'
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header; 